import { takeLatest, put, call, select } from 'redux-saga/effects';
import axios from 'axios';

// constants for endpoints
import {
  AUTHENTICATION,
  BONUS_API,
} from '../../../components/global/Server/endpoints';

// constants for actions
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  GET_EMPLOYEES,
  GET_ONLINE_EMPLOYEES,
  GET_USERS,
  REFRESH_USER_INFO,
  REGISTER_USER,
} from '../../constants/auth';
import {
  DELETE_RFID,
  PROCESS_RFID,
  REGISTER_RFID,
  UPLOAD_BONUS_PHOTO,
} from '../../constants/userManagement';

// secondary actions
import {
  requestStatus,
  clearStatus,
  showErrorMessage,
  hideError,
  showLicenseDialog,
} from '../../actions/global';
import { fetchLinkedUsers, storeRFID } from '../../actions/userManagement';
import {
  storeEmployees,
  storeOnlineEmployees,
  storeToken,
  storeUsers,
  updateUserInfo,
} from '../../actions/auth';
import { HTTP_SUCCESS } from '../../constants/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import AuthUtil from '../../AuthUtil';

function* getUserInfo(data, token) {
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  // Stamp the currently-selected company so the backend's
  // AccountService.getUserInfo evaluates lockState against the company
  // the user is operating in, not their home company. Without this, an
  // authoritative refresh after a 423 (refreshUserInfoSaga) reads the
  // home company's lockState and unmounts the LockedShell that the
  // optimistic 423 path just opened.
  const selectedCompanyId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('selectedCompanyId')
      : '';
  if (selectedCompanyId) {
    headers['X-Selected-Company'] = selectedCompanyId;
  }
  return yield axios
    .post(AUTHENTICATION.GET_USER_INFO, data, { headers })
    .then((response) => response.data);
}

function* request(username, password) {
  return yield axios
    .post(AUTHENTICATION.AUTH_LOGIN, {
      username,
      password,
    })
    .then((res) => res.data)
    .catch((error) => {
      let response = {};
      if (error.response) {
        response.status = error.response.status;
        response.statusText = error.response.statusText;
        response.data = error.response.data;
      }
      return response;
    });
}

function* authLogout() {
  yield put(hideError());
  try {
    yield call(() => AuthUtil.postRequest(AUTHENTICATION.AUTH_LOGOUT, {}));
    yield localStorage.clear();
    window.location.replace('/login');
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* authLogin(action) {
  yield put(hideError());
  // Wipe any stale linked-company selection from a previous user's
  // session on this browser. Without this, the first user-info / feature
  // request after login would stamp the previous user's selectedCompanyId
  // onto the X-Selected-Company header — the backend would safely fall
  // back to the home company (the helper validates membership), but it
  // pollutes logs and confuses anyone debugging.
  localStorage.removeItem('selectedCompanyId');
  try {
    const response = yield call(() =>
      request(action.username, action.password)
    );
    if (response.access_token) {
      const additionalInfo = yield call(() =>
        getUserInfo({ username: action.username }, response.access_token)
      );

      localStorage.setItem('token', response.access_token);
      // Seed the selected company to the user's home so the very next
      // request after login carries a valid X-Selected-Company header.
      // CompanyPicker overwrites this on switch.
      if (additionalInfo?.companyId) {
        localStorage.setItem('selectedCompanyId', additionalInfo.companyId);
      }

      const userInfo = {
        username: response.username,
        authority: response.roles,
        firstname: additionalInfo.firstname,
        lastname: additionalInfo.lastname,
        userId: additionalInfo.userId,
        company: additionalInfo.companyId,
        linkedUsers: additionalInfo.linkedUsers,
        routes: additionalInfo.routes,
        userType: additionalInfo.userType,
        employeeColor: additionalInfo.employeeColor,
        companyIds: additionalInfo.companyIds,
        selectedLocations: additionalInfo.selectedLocations,
        defaultLocationId: additionalInfo.defaultLocationId,
        selectedLocationId: additionalInfo.companyId,
        isOwner: additionalInfo.isOwner,
        selectedLocationName: additionalInfo.selectedLocationName,
        planName: additionalInfo.planName,
        // Drives the trial-grace COUNTDOWN BANNER for users still inside
        // the 3-day window. Once revoked, the locked shell takes over
        // via lockState (below).
        trialGrace: additionalInfo.trialGrace || null,
        // Single source of truth for "is this user locked". Backend
        // builds this from company.enabled / status / revokedReason and
        // the user's roles. Drives <LockedShell /> in Base.jsx and is
        // refreshed on every 423 response.
        lockState: additionalInfo.lockState || null,
      };

      yield put(storeToken(response.access_token, userInfo));

      // Define who counts as an Admin
      const isAdmin =
        response.roles.includes('ROLE_ADMIN') ||
        response.roles.includes('ROLE_MASTER_ADMIN');

      // A user is a *dedicated* security person only if their role
      // bundle is the Security preset — i.e. they have ROLE_SECURITY
      // but not the operational dashboard roles that Manager / Super-
      // visor / Employee always get (ROLE_CUSTOMER from the Dashboard
      // section). Without this check, Managers and Supervisors who
      // have the Security section enabled would also be redirected
      // to /security on login.
      const hasSecurityRole = response.roles.includes('ROLE_SECURITY');
      const hasCustomerAccess = response.roles.includes('ROLE_CUSTOMER');
      const isDedicatedSecurity = hasSecurityRole && !hasCustomerAccess;

      if (isDedicatedSecurity && !isAdmin) {
        // Dedicated Security role: land on the security dashboard.
        yield action.history.push('/security');
      } else if (isAdmin) {
        // Admins go to the main dashboard/home even if they have the security role
        yield action.history.push('/');
      } else {
        // Everyone else
        yield action.history.push('/');
      }
    } else {
      if (response.data.includes('Your company account has expired.')) {
        yield put(showLicenseDialog());
      }
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerUser(action) {
  yield call(clearStatus);

  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.AUTH_REGISTER, action.newUser)
    );

    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === 200,
    };

    yield put(requestStatus(status));
    if (status.success && action.newUser.is_linked_user) {
      const userAction = {
        locationId: action.newUser.selectedLocationId,
      };
      yield put(fetchLinkedUsers(userAction));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getUsersSaga() {
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(AUTHENTICATION.GET_USERS)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeUsers(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getOnlineEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_ONLINE_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeOnlineEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.REGISTER_RFID, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.message);
      }
      yield put(storeRFID(response.data.user));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* processRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.PROCESS_RFID, action.data)
    );
    if (action.callback && typeof action.callback === 'function') {
      action.callback(response.data.success, response.data.message);
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* uploadBonusPhotoSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postUploadRequest(
        BONUS_API.UPLOAD_BONUS_PHOTO,
        action.data,
        (progress) => {
          if (action.dispatchProgress) action.dispatchProgress(progress);
        }
      )
    );
    if (response.data.success) {
      if (action.callback) action.callback(true, 'Saved');
      if (action.dispatchProgress) action.dispatchProgress(0); // reset progress
    }
  } catch (error) {
    if (action.callback) action.callback(false, 'Server failed');
    yield put(showErrorMessage('Server failed'));
    if (action.dispatchProgress) action.dispatchProgress(0); // reset progress
  }
}

function* deleteRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.DELETE_RFID, action.data)
    );
    if (response.data.success) {
      yield put(storeRFID(response.data.user));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* refreshUserInfoSaga() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    const currentUserInfo = yield select((state) => state.auth?.userInfo);
    const username = currentUserInfo?.username;
    if (!username) return;
    const additionalInfo = yield call(() => getUserInfo({ username }, token));
    if (!additionalInfo) return;
    // UPDATE_USER_INFO replaces the userInfo object outright, so merge
    // with the current state to avoid wiping fields the saga doesn't
    // know about (routes, companyIds, selectedLocationName, etc.).
    // lockState is included so a periodic refresh transitions the user
    // to / from the locked shell in-place without requiring re-login.
    yield put(
      updateUserInfo({
        ...currentUserInfo,
        trialGrace: additionalInfo.trialGrace || null,
        planName: additionalInfo.planName,
        lockState: additionalInfo.lockState || null,
      })
    );
  } catch (e) {
    // Silent — a missed refresh just means the banner shows stale state
    // until the next login.
  }
}

export default function* authSagas() {
  yield takeLatest(AUTH_LOGIN, authLogin);
  yield takeLatest(REFRESH_USER_INFO, refreshUserInfoSaga);
  yield takeLatest(REGISTER_USER, registerUser);
  yield takeLatest(AUTH_LOGOUT, authLogout);
  yield takeLatest(GET_USERS, getUsersSaga);
  yield takeLatest(REGISTER_RFID, registerRFIDSaga);
  yield takeLatest(DELETE_RFID, deleteRFIDSaga);
  yield takeLatest(PROCESS_RFID, processRFIDSaga);
  yield takeLatest(GET_EMPLOYEES, getEmployeesSaga);
  yield takeLatest(GET_ONLINE_EMPLOYEES, getOnlineEmployeesSaga);
  yield takeLatest(UPLOAD_BONUS_PHOTO, uploadBonusPhotoSaga);
}
